import { useEffect, useRef, useState } from 'react';
import { getLenis } from '../hooks/useLenis.js';
import { createFrameClock, smoothingFactor } from '../lib/timeSmoothing.js';

// Seconds the band takes to drift through one copy of its words at rest.
const LOOP_SECONDS = 80;
// Scroll speed that adds one base speed to the drift. Measured in px per
// second, so every refresh rate gets the same push; 1500 matches the old
// per-frame tuning (velocity × 0.08) on a 120Hz screen.
const BOOST_PX_PER_SEC = 1500;
// How quickly the band eases toward the speed the scroll asks for: the old
// per-frame 0.08 lerp at 120Hz, as a time constant.
const BOOST_EASE_SECONDS = 0.1;
// Cap the boost so wild flicks don't make the band tear visually.
const BOOST_MIN = -2.5;
const BOOST_MAX = 3.5;
// One copy to show, one to slide in behind it.
const MIN_COPIES = 2;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// Lenis's velocity is how far the page moved in its latest update: one step of
// its own loop while it smooths a wheel scroll, or one scroll event while the
// browser scrolls natively (touch, scrollbar, keyboard), in which case Lenis
// keeps the value until the next event and clears it 400ms after the last.
// Browsers fire at most one scroll event per frame, so dividing by the frame's
// length (the gap between consecutive `time` stamps) gives px per second for
// any scroll that moves every frame, whatever the refresh rate and whether the
// band's loop runs before or after Lenis's within a frame.
function createScrollSpeedReader(readLenis) {
  let lenis = null;
  let lastTime = 0;
  let pxPerSec = 0;
  return () => {
    const current = readLenis();
    if (current !== lenis) {
      lenis = current;
      lastTime = current ? current.time : 0;
      pxPerSec = 0;
      return 0;
    }
    if (lenis && lenis.time > lastTime) {
      const speed = lenis.velocity / ((lenis.time - lastTime) / 1000);
      // A non-number would stay in the eased boost for good and freeze the
      // band; count it as no scroll, so the band keeps its base drift.
      pxPerSec = Number.isFinite(speed) ? speed : 0;
      lastTime = lenis.time;
    }
    return pxPerSec;
  };
}

// How many copies keep the band covered once the track has slid left by a
// full loop. The first copy's words end at `firstCopyEnd`; every copy after it
// ends exactly one loop further right.
function copiesToCover(bandWidth, firstCopyEnd, loopWidth) {
  return Math.max(MIN_COPIES, 2 + Math.ceil((bandWidth - firstCopyEnd) / loopWidth));
}

// Velocity-driven marquee. Base scroll is a slow constant drift; user scroll
// velocity (from the shared Lenis instance) is added on top with a smoothed
// ease so the band visibly accelerates when the user scrolls down, decelerates
// when they scroll up, and gently returns to base when they stop. The band
// renders its own copies of the words, so the loop is complete however the
// page was reached.
export function Marquee({ items }) {
  const bandRef = useRef(null);
  const trackRef = useRef(null);
  const loopWidthRef = useRef(0);
  const [copies, setCopies] = useState(MIN_COPIES);

  // Measure one loop and the copies needed whenever the band or its words
  // change size (web fonts landing, viewport resizes, copies added).
  useEffect(() => {
    const band = bandRef.current;
    const track = trackRef.current;
    // Widths in layout px, as the observer reports them.
    let bandWidth = 0;
    let trackWidth = 0;
    const measure = (entries) => {
      for (const entry of entries) {
        const width = entry.borderBoxSize[0].inlineSize;
        if (entry.target === band) bandWidth = width;
        else trackWidth = width;
      }
      const words = track.children;
      const firstOfSecondCopy = words[items.length];
      if (!firstOfSecondCopy) return;
      // Client rects also carry any transform on an ancestor (a scale-in
      // reveal, say), but the band moves the track in layout px: convert word
      // positions with the track's layout width over its client width.
      const trackRect = track.getBoundingClientRect();
      const toLayoutPx = trackWidth / trackRect.width;
      const rightEdge = (word) =>
        (word.getBoundingClientRect().right - trackRect.left) * toLayoutPx;
      // Right edges: every later copy opens with the bullet that separates it
      // from the previous one, the very first word has none.
      const loopWidth = rightEdge(firstOfSecondCopy) - rightEdge(words[0]);
      // Hidden or not laid out yet (zero or NaN): keep the band still until a
      // later resize brings real sizes.
      if (!(loopWidth > 0)) return;
      loopWidthRef.current = loopWidth;
      // Read off the words, not the track's box, so the count holds however
      // wide that box is laid out.
      const firstCopyEnd = rightEdge(words[items.length - 1]);
      setCopies(copiesToCover(bandWidth, firstCopyEnd, loopWidth));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(band);
    ro.observe(track);
    return () => ro.disconnect();
  }, [items.length]);

  useEffect(() => {
    const track = trackRef.current;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      // Static layout — no animation at all under reduce-motion.
      track.style.transform = 'translateX(0)';
      return undefined;
    }

    const readScrollSpeed = createScrollSpeedReader(getLenis);
    const frameSeconds = createFrameClock();
    let position = 0;
    let boost = 0;
    let rafId = 0;

    const tick = (now) => {
      rafId = requestAnimationFrame(tick);
      const dt = frameSeconds(now);

      // Scrolling down speeds up the leftward drift; scrolling up can briefly
      // reverse the band.
      const target = clamp(readScrollSpeed() / BOOST_PX_PER_SEC, BOOST_MIN, BOOST_MAX);
      boost += (target - boost) * smoothingFactor(dt, BOOST_EASE_SECONDS);

      const loopWidth = loopWidthRef.current;
      if (loopWidth > 0) {
        position -= (loopWidth / LOOP_SECONDS) * (1 + boost) * dt;
        // Wrap within one loop; the copies are identical, so the jump is invisible.
        position = (((position % loopWidth) + loopWidth) % loopWidth) - loopWidth;
      }
      track.style.transform = `translate3d(${position}px, 0, 0)`;
    };
    rafId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <section className="marquee reveal" ref={bandRef}>
      <div className="marquee-track" ref={trackRef}>
        {/* Only the first copy is read out; the rest just fill the loop. */}
        {Array.from({ length: copies }).flatMap((_, copy) =>
          items.map((word, i) => (
            <span key={`${copy}-${i}`} aria-hidden={copy > 0 || undefined}>
              {word}
            </span>
          ))
        )}
      </div>
    </section>
  );
}
